/**
 * CockpitClassDashboardCard — tiny dashboard surface that pins the
 * latest published Super Cockpit Class lesson so users notice the
 * weekly tutorial cadence without digging into a separate route.
 *
 * Self-hides if no lesson is published yet. One GET only — cheap.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { GraduationCap, ArrowRight } from 'lucide-react';
import { API_URL } from '../config/api';

export const CockpitClassDashboardCard = () => {
  const [lesson, setLesson] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/cockpit-class/lessons`);
        if (!alive) return;
        setLesson(r.data?.lessons?.[0] || null);
      } catch {
        if (alive) setLesson(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!lesson) return null;

  return (
    <Card
      data-testid="cockpit-class-dashboard-card"
      className="border-emerald-500/30 bg-gradient-to-r from-emerald-50/80 via-background to-background dark:from-emerald-950/30"
    >
      <CardContent className="py-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="text-3xl leading-none">{lesson.hero_emoji || '🌱'}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-semibold mb-1">
              <GraduationCap className="w-3.5 h-3.5" /> This week's class
              {lesson.metric_tag && (
                <Badge variant="outline" className="text-[9px] border-emerald-500/40 text-emerald-700 dark:text-emerald-300">
                  {lesson.metric_tag}
                </Badge>
              )}
            </div>
            <div className="font-semibold text-foreground">{lesson.title}</div>
            <div className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
              {lesson.teaser_md || lesson.dek}
            </div>
          </div>
          <Link to={`/cockpit/class/${lesson.slug}`}>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="cockpit-class-dashboard-cta">
              Read <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default CockpitClassDashboardCard;
